import React, { useState } from 'react';
import { 
  FileText, 
  DollarSign, 
  Users, 
  Video, 
  ClipboardList, 
  Mail,
  Brain,
  Shield
} from 'lucide-react';

// Import the images
let FreeDiscountedImage: any;
try {
  FreeDiscountedImage = require('../assets/free-discounted.930cbc44.jpg');
} catch {
  FreeDiscountedImage = null;
}

let PremiumServiceImage: any;
try {
  PremiumServiceImage = require('../assets/premium-serv.41305c96.jpg');
} catch {
  PremiumServiceImage = null;
}

let TherapistOptionsImage: any;
try {
  TherapistOptionsImage = require('../assets/therapist-options.4b9b2128.png');
} catch {
  TherapistOptionsImage = null;
}

let OnlineInPersonImage: any;
try {
  OnlineInPersonImage = require('../assets/on-on-one.d54a3c8f.png');
} catch {
  OnlineInPersonImage = null;
}

let WrittenReportImage: any;
try {
  WrittenReportImage = require('../assets/written-report.2f3aad74.jpg');
} catch {
  WrittenReportImage = null;
}

let TreatmentConfirmationImage: any;
try {
  TreatmentConfirmationImage = require('../assets/treatment-confirmation.3cf99261.png');
} catch {
  TreatmentConfirmationImage = null;
}

let TwinBrainImage: any;
try {
  TwinBrainImage = require('../assets/twin-brain.ed2582ad.jpg');
} catch {
  TwinBrainImage = null;
}

let HeroImage: any;
try {
  HeroImage = require('../assets/serv-hero-sec.274eb20e.png');
} catch {
  HeroImage = null;
}

const ServicesPage: React.FC = () => {
  const [expandedService, setExpandedService] = useState<number | null>(null);

  const services = [
    {
      title: "Free & Discounted Therapy",
      description: "At HealthCity, we take great pride in providing end-to-end solutions to our clients. People who are looking for free, low cost or discounted well-being and mental health service providers can find the right professional for their needs using our free and discounted therapy services. You can locate qualified counselors, cognitive-behavioral therapists, psychologists, fitness coaches, and other professionals, from your locality or around the world, who can guide you to improve the standard of your life.",
      expandedContent: "Our dedicated team of licensed professionals includes experienced therapists, psychologists, and counselors who specialize in various areas of mental health. Whether you're dealing with anxiety, depression, relationship issues, or life transitions, we have experts ready to help. We work with insurance providers and offer sliding scale fees to ensure that quality mental health care is accessible to everyone, regardless of their financial situation. Our commitment is to remove barriers to treatment and provide compassionate, evidence-based care to all who need it.",
      icon: Shield,
      imagePosition: "left",
      image: FreeDiscountedImage,
      readMore: true
    },
    {
      title: "Premium Service",
      description: "According to the needs of our diverse clients, we offer ultra-luxury, deluxe, and premium services available for each business and all persons not covered by insurance. We come up with many options for personalized therapy sessions tailored to address individual problems. For the middle-class, the excellent assessments, the top-notch expert professional guidance and personalized approaches that we provide are readily accessible. For Couples Therapy, Psychotherapy, and other accredited professions, from the leaders for clinician care.",
      icon: DollarSign,
      imagePosition: "right",
      image: PremiumServiceImage
    },
    {
      title: "A Range Of Professionals Available",
      description: "No matter the issue, at HealthCity we have a diverse selection of professionals ready to assist. From therapists and coaches to psychologists and psychiatrists, each professional we work with has been certified and fully trained to help with a range of mental health challenges and treatment methods. Our specialists are fluent in offering therapy through couples therapy, psychiatric clinical services, family therapy, and child and adolescent psychology, giving us the tools to offer professional service across 90+ issues.",
      icon: Users,
      imagePosition: "left",
      image: TherapistOptionsImage
    },
    {
      title: "Online & In-Person Sessions Available",
      description: "HealthCity offers you the option of conducting interactive online and face-to-face sessions. Our virtual and in-person sessions are perfectly balanced to get the support that you need, when you need it. Our UK-based clinics offer flexibility for busy or distant clients to enable others to receive help no matter how you prefer to get assistance. The rest of offering this information is a comprehensive hybrid approach that empowers you to get the treatment that works best with your lifestyle.",
      icon: Video,
      imagePosition: "right",
      image: OnlineInPersonImage
    },
    {
      title: "Detailed Written Report",
      description: "As a member of our clinic, you will receive from trained clinicians clear and comprehensive written reports of your medical services and treatment offered. These reports detail findings in a single understandable, transparent and easy-to-digest format, describe the treatment set up, your diagnostic outcomes and relevant clinical reasoning. You can easily access these detailed reports as well as information about your available care, professional results, and progress, which can be very useful in the future.",
      icon: ClipboardList,
      imagePosition: "left",
      image: WrittenReportImage
    },
    {
      title: "Confirmation Of Participation Letter",
      description: "HealthCity takes effort in giving you the most comfortable environment that helps you to prove with clinical therapists that you're making appropriate decisions. Proof of your participation in our treatment services may help furnish us with these documents and letters regarding patients. We provide evidence of our professional work, all services and help with key structural and clinical practices. Should you want to recommend that a therapist or assessment you received with us, we are ready to assist you.",
      icon: Mail,
      imagePosition: "right",
      image: TreatmentConfirmationImage
    },
    {
      title: "Twin Brain",
      description: "Health assessments for all life stages. We understand and have helped a lot of people with their brain from neurologically different perspectives and helped to determine Autism and ADHD. All these diverse and long-standing disorders are now receptive along with other assessments for help. Many resources are available in the UK. Specialist doctors help facilitate comprehensive brain processing assessments that show innovative guidelines for helping with mental health.",
      icon: Brain,
      imagePosition: "left",
      image: TwinBrainImage
    },
    {
      title: "Resource Hub X",
      description: "To be healthy, having support for diagnosed mental health conditions or disorders (depression, PTSD, anxiety, addiction recovery therapy), our patients with a wide range may find the professional support needed for your success.",
      icon: FileText,
      imagePosition: "right",
      image: TwinBrainImage
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section with Background Image */}
      <div 
        className="relative bg-cover bg-center py-24 text-center"
        style={{ backgroundImage: HeroImage ? `url(${HeroImage})` : 'none' }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Mental Health & Wellness Services And Sessions
          </h1>
          <h2 className="text-xl md:text-2xl text-white font-light">
            Learn to monitor, manage, and improve your mental health with guided professional help.
          </h2>
        </div>
      </div>

      {/* Services Sections */}
      <div className="max-w-7xl mx-auto px-4 py-16 space-y-24">
        {services.map((service, index) => (
          <div 
            key={index}
            className={`relative flex flex-col lg:flex-row items-stretch ${
              service.imagePosition === 'right' ? 'lg:flex-row-reverse' : ''
            }`}
          >
            {/* Large Image Box */}
            <div className="w-full lg:w-[55%] h-80 bg-gray-700 rounded-lg overflow-hidden relative z-0">
              {service.image ? (
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              ) : null}
            </div>

            {/* Content Area - Overlapping the image */}
            <div className={`w-full lg:w-[50%] relative ${
              service.imagePosition === 'right' 
                ? 'lg:mr-[-5%]' 
                : 'lg:ml-[-5%]'
            } z-10 mt-8 lg:mt-0 flex items-center`}>
              {/* Text Content Box (White with shadow) */}
              <div className="bg-white rounded-lg p-8 shadow-lg w-full">
                <h2 className="text-2xl font-bold text-teal-700 mb-4">
                  {service.title}
                </h2>
                <p className="text-gray-700 leading-relaxed text-sm mb-4">
                  {service.description}
                </p>
                {expandedService === index && (service as any).expandedContent && (
                  <p className="text-gray-700 leading-relaxed text-sm mb-4">
                    {(service as any).expandedContent}
                  </p>
                )}
                {service.readMore && (
                  <button 
                    onClick={() => setExpandedService(expandedService === index ? null : index)}
                    className="text-teal-600 font-semibold hover:text-teal-700 transition-colors"
                  >
                    {expandedService === index ? 'Read Less' : 'Read More'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesPage;